<script>
import { formatDatePretty, humanReadableHours, formatWeekDay } from '@/utils';
import IconEye from '@/components/icons/Eye';
import IconGhost from '@/components/icons/Ghost';
import IconPencil from '@/components/icons/Pencil';

const propTypes = {
  data: {
    type: Array,
    required: true,
  },
  linkBuilder: {
    type: Function,
    default: v => v,
  },
  canReadWorker: {
    type: Boolean,
    default: false,
  },
  canReadProject: {
    type: Boolean,
    default: false,
  },
  canReadTask: {
    type: Boolean,
    default: false,
  },
  canEdit: {
    type: Boolean,
    default: false,
  },
  dateIsWeekend: {
    type: Function,
    default: () => false,
  },
};

const LogsList = ({ $style, data, props }) => (
  <div class={[data.class, data.staticClass]}>
    {Array.isArray(props.data) && props.data.length ? (
      props.data.map(group => (
        <div
          key={group.date}
          class={$style.group}
        >
          <div class={$style.groupHeader}>
            <span class={$style.groupHeaderDate}>
              {formatDatePretty(group.date)}
              {props.dateIsWeekend(group.date) && (
                ` - выходной (${formatWeekDay(group.date)})`
              )}
            </span>

            <span class={$style.groupHeaderTotal}>
              <b>
                {humanReadableHours(group.totalHours)}
              </b>
            </span>
          </div>

          <div class={[$style.groupBody, $style.log]}>
            {group.items.map(item => (
              <div
                key={item.id}
                class={$style.logRow}
              >
                <div class={[$style.logCell, $style.logCell_action]}>
                  <router-link
                    to={props.linkBuilder(`/logs/${item.id}`)}
                    class={$style.logDetailsLink}
                  >
                    {props.canEdit
                      ? <IconPencil />
                      : <IconEye />}
                  </router-link>
                </div>
                <div class={[$style.logCell, $style.logCell_worker]}>
                  {props.canReadWorker
                    ? (
                      <router-link to={`/workers/${item.worker.id}`}>
                        {item.worker.name}
                      </router-link>
                    )
                    : (
                      <span>
                        {item.worker.name}
                      </span>
                    )}
                </div>
                <div class={[$style.logCell, $style.logCell_project]}>
                  {props.canReadProject
                    ? (
                      <router-link to={`/projects/${item.project.id}`}>
                        {item.project.name}
                      </router-link>
                    )
                    : (
                      <span>
                        {item.project.name}
                      </span>
                    )}
                </div>
                <div class={[$style.logCell, $style.logCell_task]}>
                  {item.taskNumber && (
                    <b>
                      {`${item.taskNumber} `}
                    </b>
                  )}
                  {props.canReadTask ? item.task : ''}
                </div>
                <div class={[$style.logCell, $style.logCell_hours]}>
                  <b>
                    {humanReadableHours(item.hours)}
                  </b>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))
    ) : (
      <div
        key="_empty-state"
        class={$style.empty}
      >
        <div class={$style.emptyIcon}>
          <IconGhost />
        </div>
        ничего не найдено
      </div>
    )}
  </div>
);

LogsList.props = propTypes;

export default LogsList;
</script>

<style lang="scss" module>
@import '@/theme/include.scss';

.group {
  position: relative;

  &Header {
    background-color: $color-white;
    border-bottom: 1px solid $color-gray-darker;
    display: flex;
    justify-content: space-between;
    padding: 16px 16px 8px;
    position: sticky;
    top: 0;
  }
}

.log {
  font-size: 14px;

  &Row {
    display: flex;

    &:nth-child(2n+1) {
      background-color: $color-gray-lightest;
    }

    &:hover,
    &:focus-within {
      background-color: $color-gray-lighter;
    }
  }

  &Cell {
    flex-grow: 0;
    flex-shrink: 0;
    padding: 12px 16px;

    &_action {
      padding-right: 0;
    }

    &_worker {
      box-sizing: content-box;
      width: 160px;
    }

    &_project {
      box-sizing: content-box;
      overflow-x: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 120px;
    }

    &_task {
      flex-grow: 1;
      flex-shrink: 1;
      white-space: initial;
    }

    &_hours {
      margin-left: auto;
    }
  }

  &DetailsLink {
    display: inline-block;
    opacity: 0;
  }

  &Row:hover &DetailsLink,
  &Row:focus-within &DetailsLink {
    opacity: 1;
  }
}

.empty {
  align-items: center;
  color: $color-gray;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 32px;
  text-align: center;

  &Icon {
    color: $color-gray-lighter;
    font-size: 5em;
    margin: .125em 0;
  }
}
</style>
